/**
 * デバッグモーダル群
 * 歯科医システムプロンプトモーダルとアバターデバッグ情報モーダル
 */

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { User } from "lucide-react";

export interface DebugModalsProps {
  // 歯科医システムプロンプトモーダル
  isDentistSystemPromptModalOpen: boolean;
  setIsDentistSystemPromptModalOpen: (open: boolean) => void;
  dentistSystemPrompt: string;
  dentistName: string;
  // デバッグモーダル
  isDebugModalOpen: boolean;
  setIsDebugModalOpen: (open: boolean) => void;
  debugInfo: any;
}

export default function DebugModals({
  isDentistSystemPromptModalOpen,
  setIsDentistSystemPromptModalOpen,
  dentistSystemPrompt,
  dentistName,
  isDebugModalOpen,
  setIsDebugModalOpen,
  debugInfo,
}: DebugModalsProps) {
  return (
    <>
      {/* 歯科医システムプロンプトモーダル（裏モード） */}
      <Dialog open={isDentistSystemPromptModalOpen} onOpenChange={setIsDentistSystemPromptModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              歯科医アバターシステムプロンプト（裏モード）
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-600 mb-2">
                <strong>歯科医アバター:</strong> {dentistName}
              </div>
              <div className="text-sm text-gray-600 mb-4">
                このシステムプロンプトは、歯科医アバターがユーザー（歯科衛生士）との対話で使用されます。
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold mb-2">システムプロンプト:</div>
              <pre className="text-xs bg-gray-100 p-4 rounded overflow-auto whitespace-pre-wrap border border-gray-300">
                {dentistSystemPrompt || 'システムプロンプトが設定されていません'}
              </pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* デバッグモーダル（裏モード） */}
      <Dialog open={isDebugModalOpen} onOpenChange={setIsDebugModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              アバタープロフィール・ストーリー情報（裏モード）
            </DialogTitle>
          </DialogHeader>

          {debugInfo && (
            <div className="space-y-6">
              {/* プロフィール情報 */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">プロフィール</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div><strong>ID:</strong> {debugInfo.profile.id}</div>
                    <div><strong>名前:</strong> {debugInfo.profile.name}</div>
                    <div><strong>ローマ字名:</strong> {debugInfo.profile.romanName}</div>
                    <div><strong>ひらがな名:</strong> {debugInfo.profile.hiraganaName}</div>
                    <div><strong>年齢:</strong> {debugInfo.profile.age}歳</div>
                    <div><strong>性別:</strong> {debugInfo.profile.gender}</div>
                    <div><strong>職業:</strong> {debugInfo.profile.occupation}</div>
                    <div><strong>居住地:</strong> {debugInfo.profile.residence}</div>
                    <div><strong>学歴:</strong> {debugInfo.profile.education}</div>
                    <div><strong>家族構成:</strong> {debugInfo.profile.family}</div>
                    <div><strong>収入:</strong> {debugInfo.profile.income}</div>
                    <div><strong>主な業務:</strong> {debugInfo.profile.mainWork}</div>
                    <div className="col-span-2"><strong>日常パターン:</strong> {debugInfo.profile.dailyPattern}</div>
                    <div className="col-span-2"><strong>モチベーション:</strong> {debugInfo.profile.motivation}</div>
                    <div className="col-span-2"><strong>課題:</strong> {debugInfo.profile.painPoints}</div>
                    <div className="col-span-2"><strong>使用ツール:</strong> {debugInfo.profile.tools}</div>
                    <div className="col-span-2"><strong>性格:</strong> {debugInfo.profile.personality}</div>
                    {debugInfo.profile.visitHistory && (
                      <div className="col-span-2"><strong>来院歴:</strong> {debugInfo.profile.visitHistory}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
